import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import type {
  LoanSectionDef,
  LoanSectionKey,
} from '@/config/loanProductRegistry';
import { useSignLoanAttestationMutation } from '@/apis/extended/loanApplicationApi';
import {
  isLoanAttestationSupersededByEdits,
  loanAttestationIsCurrent,
  loanAttestationNeedsRecording,
} from '@/utils/loanApplicationAttestation';
import type { LoanApplication } from '@/types/LoanApplicationType';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
  /** Answer sections still open — signing waits until these are done. */
  incompleteSections?: LoanSectionDef[];
  onGoToSection?: (key: LoanSectionKey) => void;
};

const TERMS_VERSION = '1.0';

export function SignSection({
  application,
  readOnly,
  incompleteSections = [],
  onGoToSection,
}: Props) {
  const [sign, { isLoading }] = useSignLoanAttestationMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [typedName, setTypedName] = useState('');
  const nameOk = typedName.trim().replace(/\s+/g, ' ').length >= 2;

  useEffect(() => {
    if (readOnly) setDialogOpen(false);
  }, [readOnly]);

  const current = loanAttestationIsCurrent(application);
  const superseded = isLoanAttestationSupersededByEdits(application);
  const needsRecording = loanAttestationNeedsRecording(application);

  // Signing says the answers are true and complete — that can't be said of a
  // half-finished application.
  const blocked = incompleteSections.length > 0;

  const openFlow = () => {
    if (readOnly || blocked) return;
    setConfirmed(false);
    setTypedName('');
    setDialogOpen(true);
  };

  const closeFlow = () => {
    setDialogOpen(false);
    setConfirmed(false);
  };

  const onRecordAttestation = async () => {
    if (readOnly || !confirmed || !nameOk) return;
    try {
      await sign({
        id: application._id,
        acceptedTermsVersion: TERMS_VERSION,
        typedName: typedName.trim(),
      }).unwrap();
      toast.success('Signed');
      closeFlow();
    } catch {
      toast.error('Could not save your signature. Please try again.');
    }
  };

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Confirm your answers are true and complete. You can keep editing
        afterward — if you change an answer, just sign again before you submit.
      </Typography>

      {current && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Signed
          {application.signAttestation?.typedName
            ? ` by ${application.signAttestation.typedName}`
            : ''}{' '}
          —{' '}
          {application.signAttestation?.acceptedAt
            ? new Date(application.signAttestation.acceptedAt).toLocaleString()
            : '—'}
          {' — '}
          terms v
          {application.signAttestation?.acceptedTermsVersion ?? TERMS_VERSION}
        </Alert>
      )}

      {superseded && application.signAttestation?.acceptedAt && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The application was updated after your last attestation (
          {new Date(application.signAttestation.acceptedAt).toLocaleString()}
          ). Please complete attestation again so your file reflects the latest
          information.
        </Alert>
      )}

      {!readOnly && needsRecording && blocked && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Finish these sections first, then sign:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {incompleteSections.map((s) => (
              <Button
                key={s.key}
                size="small"
                variant="outlined"
                onClick={() => onGoToSection?.(s.key)}
              >
                {s.label}
              </Button>
            ))}
          </Box>
        </Alert>
      )}

      {!readOnly && needsRecording && (
        <Button
          variant="contained"
          color="primary"
          onClick={openFlow}
          disabled={blocked}
        >
          Review & sign
        </Button>
      )}

      {!readOnly && current && (
        <Button variant="outlined" sx={{ ml: 1 }} onClick={openFlow}>
          Sign again
        </Button>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeFlow}
        maxWidth="sm"
        fullWidth
        aria-labelledby="loan-attestation-dialog-title"
      >
        <DialogTitle id="loan-attestation-dialog-title">
          Sign your application
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            You are attesting under the terms of use for this product (version{' '}
            <strong>{TERMS_VERSION}</strong>) that the information in this
            application is true and complete to the best of your knowledge, as
            of the time you submit.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            If you change any answers after submitting, your previous
            attestation will no longer apply until you complete this step again.
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(_, c) => setConfirmed(c)}
                  color="primary"
                />
              }
              label="I have reviewed the information above and agree to record this attestation."
            />
          </Box>
          <TextField
            label="Type your full legal name to sign"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            fullWidth
            autoComplete="name"
            inputProps={{ maxLength: 200, 'aria-label': 'Full legal name' }}
            helperText="Typing your name here is your electronic signature."
            sx={{ mt: 2 }}
            InputProps={{
              sx: { fontFamily: '"Brush Script MT", cursive', fontSize: 22 },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeFlow} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void onRecordAttestation()}
            disabled={readOnly || isLoading || !confirmed || !nameOk}
          >
            {isLoading ? 'Signing…' : 'I agree — sign'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
